import { ModuleTree } from "vuex";
import { AuthenticationService } from "@/services/authentication.service";
import { AuthenticationCredentials } from "@/interfaces/authentication-credentials";
import { jwtRefreshTokenKey, jwtService, jwtStorageKey } from "@/jwt";
import { AuthenticationResponse } from "@/interfaces/authentication-response";

/**
 * @author Damian Kajzer <damian.kajzer@sowlgroup.com>
 * Vuex authentication module
 */
export const AuthenticationModule: ModuleTree<unknown> = {
  auth: {
    namespaced: true,
    state: {
      jwt: null,
      refreshToken: null,
      username: ""
    },
    actions: {
      /**
       * Triggers authentication request.
       * If it is successful then propagates JWT to browser's localStorage and calls authSuccess mutation.
       * @param commit Vuex commit
       * @param credentials User credentials
       */
      authenticate({ commit }, credentials: AuthenticationCredentials) {
        AuthenticationService.authenticate(credentials).then(response => {
          commit("authSuccess", response.data);
        });
      },

      /**
       * Refreshes JWT token
       * @param commit Vuex commit
       * @param state Vuex state
       */
      refreshToken({ commit, state }) {
        return AuthenticationService.refreshToken(state.refreshToken).then(
          response => {
            commit("authSuccess", response.data);
          }
        );
      },

      /**
       * Performs logout steps in app logic.
       * Removes JWT from browser's Web Storage and Vuex state, then redirects to legacy application URL.
       * @param commit Vuex commit
       */
      logout({ commit }) {
        commit("authStateClear");
        window.location.href = `${process.env.VUE_APP_LEGACY_APP_URL}/index.php?do=logout`;
      }
    },
    mutations: {
      /**
       * Saves authenticated user's data to Vuex
       * @param state Vuex state
       * @param data Authentication data
       */
      authSuccess(state, data: AuthenticationResponse) {
        const { token, refreshToken } = data;
        localStorage.setItem(jwtStorageKey, token);
        localStorage.setItem(jwtRefreshTokenKey, refreshToken);
        state.jwt = token;
        state.refreshToken = refreshToken;
        state.username = jwtService.payload.username;
      },

      /**
       * Clears authenticated user's data in Vuex
       * @param state Vuex state
       */
      authStateClear(state) {
        localStorage.removeItem(jwtStorageKey);
        localStorage.removeItem(jwtRefreshTokenKey);
        state.jwt = null;
        state.refreshToken = null;
        state.username = null;
      }
    }
  }
};
